/** Copyright (c) 2019 T.Rowe Price. All rights reserved. */

import isEmpty from 'lodash/isEmpty';
import every from 'lodash/every';
import isNull from 'lodash/isNull';
import cloneDeep from 'lodash/cloneDeep';
import { createSelector } from 'reselect';

import Time from 'services/Time';
import {
  get529Accounts,
  getAccounts,
  getOwnedActiveAccounts,
  getActiveOwnedAccountsWithWorkplace,
  getActivePlusAccounts,
  getAlaska529Accounts,
  getAllAccounts,
  getBrokerageAccounts,
  getCheckwritingAccountGroups,
  getCurrentOwnedAccounts,
  getHasWorkPlaceRetirementAccountsOnly,
  getMaryland529Accounts,
  getMutualFundAccounts,
  getNational529Accounts,
  getNonPurgedAccounts,
  getNonRetirementAccounts,
  getVisibleRetirementAccounts,
  hasOwnedAlaska529,
  hasOwnedMaryland529,
  hasOwnedNational529,
  getHasRetailTracAccounts
} from 'state/account/AccountSelectors';
import {
  getAccountBalances,
  getRpsYearEndBalance
} from 'state/account/AccountBalancesSelectors';
import { getHasContactMethodMaintenanceVerificationIds } from 'state/contactMethodMaintenance/ContactMethodMaintenanceSelectors';
import {
  getHasDataForIrtMessagesModal,
  irtMessagesHavePriority
} from 'state/IRT/IRTMessagesSelectors';
import {
  accountRegistrationEnabled,
  contactMethodMaintenanceModalEnabled,
  custodyStatementsEnabled,
  isRMDDirectEnabled
} from 'services/FeatureFlags';
import { getRequestStatus } from 'state/requests/RequestStatusSelectors';
import { getRouter } from 'state/router/RouterSelectors';
import {
  getCustomer,
  getMailingAddress
} from 'state/customer/CustomerSelectors';
import { getRMDData } from 'state/distribution/RequiredMinimumDistributionSelectors';

import {
  getProductClass,
  hasOwnedAccounts,
  isActivePlus,
  isAlaska529,
  isAllViewOnlyAccounts,
  isBrokerage,
  isMaryland529,
  isMutualFund,
  isNational529,
  isTracPlan,
  isViewOnlyAccount
} from 'services/Products';
import { isPrimaryParty, isUGMAUTMA } from 'services/Accounts';
import {
  INDIVIDUAL,
  isIRADeceased,
  isTracIRA,
  JOINT_TENANT,
  ROTH_IRA_BENE_DEATH,
  TRADITIONAL_IRA_BENE_DEATH,
  EDUCATION_SAVINGS_ACCOUNTS
} from 'services/AccountTypes';
import { REQUEST_STATES } from 'state/requests/RequestStatusReducer';
import {
  THEME_ALASKA529,
  THEME_DEFAULT,
  THEME_MARYLAND529,
  THEME_NATIONAL529
} from 'state/view/ViewReducer';
import { minInceptionDate } from 'services/Calculation';
import { getQueryParams } from 'state/session/SessionSelectors';
import { isIFrame } from '../../support/BrowserUtils';
import { ALL_LABEL, APP_LABEL, BROKERAGE_LABEL } from 'services/Filters';
import {
  canViewContactInformation as ableToViewContactInformation,
  canViewTrustedContacts as ableToViewTrustedContacts,
  canViewBeneficiaries as ableToViewBeneficiaries,
  canViewSubscriptions as ableToViewSubscriptions,
  canViewGrantViewAccess as ableToViewGrantViewAcess,
  canViewAlertsAndNotifications as ableToViewAlertsAndNotifications
} from '@trp-ii-ii-component-library/ii-components';


const BRK_APP_OFFSET_DAYS = 1085; //155 weeks or 1085 days
export const APPLICATION_DEPENDENCIES = [
  'apiKey',
  'userAuthentication',
  'onPremise',
  'userEntitlements',
  'customer',
  'product',
  'workspacePreferences',
  'accounts',
  'purgedAccounts'
];

export const getView = (state) => state.view;

export const getPerformanceSelectedPeriod = (state) => state.selections.period;
export const getPerformanceSelectedProduct = (state) =>
  state.selections.products;
export const getPerformanceSelectedPurpose = (state) =>
  state.selections.purposes;
export const getAccountFilter = (state) => state.selections.accountFilter;
export const getPerformanceAccounts = (state) =>
  state.selections.accounts.filter((a) => !isNull(a.inceptionDate));
export const getOldestInceptionDate = (state) => state.view.oldestInceptionDate;
const periods = (state) => state.periods;

export const getCanViewYTDChange = createSelector(
    getView,
    getRpsYearEndBalance,
    getAccountBalances,
    getHasRetailTracAccounts,
    (
        view,
        rpsYearEndBalance,
        balances,
        hasRetailTracAccounts
    ) => {
        const { hasWorkplaceRetirementAccounts, hasCollegeSavingsOnly } = view;
        const { yearlyAvailable } = balances;
        if(hasRetailTracAccounts) { //when flag retailTracEnabled is off, hasRetailTracAccounts always returns false, so this if statement will be ignored
            return false;
        }
        if (hasWorkplaceRetirementAccounts && !hasCollegeSavingsOnly) {
            //If we have both RPS and Retail YTD data show
            //Still working out the details for when this data would show for the beginning of the year. This feature is currently turned off.
            return (
                rpsYearEndBalance &&
                yearlyAvailable &&
                Time.now > Time.getNinthDayOfTheYear()
            );
        }

    return yearlyAvailable && Time.now > Time.getFirstBusinessDayOfTheYear();
  }
);

export const hasDataForIrtMessagesAndCMMModals = createSelector(
  getHasContactMethodMaintenanceVerificationIds,
  getHasDataForIrtMessagesModal,
  (hasContactMethodMaintenanceVerificationIds, hasDataForIrtMessagesModal) => {
    return (
      contactMethodMaintenanceModalEnabled() &&
      hasContactMethodMaintenanceVerificationIds &&
      hasDataForIrtMessagesModal
    );
  }
);

export const isIrtMessagesNotPriority = createSelector(
  hasDataForIrtMessagesAndCMMModals,
  irtMessagesHavePriority,
  (hasDataForIrtMessagesAndCMMModals, isIrtMessagesAPriority) =>
    hasDataForIrtMessagesAndCMMModals && !isIrtMessagesAPriority
);

export const isCMMModalNotPriority = createSelector(
  hasDataForIrtMessagesAndCMMModals,
  irtMessagesHavePriority,
  (hasDataForIrtMessagesAndCMMModals, isIrtMessagesAPriority) =>
    hasDataForIrtMessagesAndCMMModals && isIrtMessagesAPriority
);

export const hasTracOnly = createSelector(getView, (view) => view.isTracOnly);

export const has529Only = createSelector(getView, (view) => view.has529Only);

export const hasCollegeSavings = createSelector(
  getView,
  (view) => view.hasCollegeSavings
);

export const hasCollegeSavingsOnly = createSelector(
  getView,
  (view) => view.hasCollegeSavingsOnly
);

export const hasActivePlus = createSelector(
  getView,
  (view) => view.hasActivePlus
);

export const hasMaryland529 = createSelector(
  getView,
  (view) => view.hasMaryland529
);

export const isDesktopLayout = createSelector(
  getView,
  (view) => view.isDesktopLayout
);

export const isMobileLayout = createSelector(
  getView,
  (view) => view.isMobileLayout
);

export const isSalesforceRepEnabled = createSelector(
  getView,
  (view) => view.isSalesforceRep && isIFrame()
);

export const hasAlaska529Only = createSelector(
  getView,
  (view) => view.hasAlaska529Only
);

export const hasAlaska529 = createSelector(
  getView,
  (view) => view.hasAlaska529
);

export const hasMaryland529Only = createSelector(
  getView,
  (view) => view.hasMaryland529Only
);

export const hasMutualFunds = createSelector(
  getView,
  (view) => view.hasMutualFunds
);

export const hasOwnedMutualFunds = createSelector(
  getView,
  (view) => view.hasOwnedMutualFunds
);

export const hasNational529 = createSelector(
  getView,
  (view) => view.hasNational529
);

export const hasRetirementAccountsOnly = createSelector(
  getView,
  (view) => view.hasRetirementAccountsOnly
);

export const hasIndividualOrJointTenant = createSelector(
  getCurrentOwnedAccounts,
  (accounts) => {
    return (
      accounts.length > 0 &&
      accounts.some(
        ({ accountTypeId }) =>
          accountTypeId === INDIVIDUAL || accountTypeId === JOINT_TENANT
      )
    );
  }
);

export const hasROTHOrTraditionalInheritedIRABene = createSelector(
  getCurrentOwnedAccounts,
  (accounts) => {
    return (
      accounts.length > 0 &&
      accounts.some(
        ({ accountTypeId }) =>
          accountTypeId === TRADITIONAL_IRA_BENE_DEATH ||
          accountTypeId === ROTH_IRA_BENE_DEATH
      )
    );
  }
);

export const hasNonRetirementAccountsOnly = createSelector(
  getView,
  (view) => view.hasNonRetirementAccountsOnly
);

export const hasRetirementAccounts = createSelector(
  getView,
  (view) => view.hasRetirementAccounts
);

export const hasWorkplaceRetirementAccounts = createSelector(
  getView,
  (view) => view.hasWorkplaceRetirementAccounts
);

export const hasEsaOnly = createSelector(getView, (view) => view.hasEsaOnly);

export const hasNational529Only = createSelector(
  getView,
  (view) => view.hasNational529Only
);

export const getHasOwnedCollegeSavings = createSelector(
  getView,
  (view) => view.hasOwnedCollegeSavings
);

export const hasBrokerage = createSelector(
  getView,
  (view) => view.hasBrokerage
);

export const isBrokerageOwner = createSelector(
  getView,
  (view) => view.isBrokerageOwner
);

export const hasBrokerageAdvantageOnly = createSelector(
  getView,
  (view) => view.hasBrokerageAdvantageOnly
);

export const hasBrokerageOnly = createSelector(
  getView,
  (view) => view.hasBrokerageOnly
);

export const hasActiveAdvisory = createSelector(
  getView,
  (view) => view.hasActiveAdvisory
);

export const hasManagedAccounts = createSelector(
  getView,
  (view) => view.hasManaged
);

export const hasManagedAccountsOnly = createSelector(
  getView,
  (view) => view.hasManagedOnly
);

export const hasActiveAdvisoryOnly = createSelector(
  getView,
  (view) => view.hasActiveAdvisoryOnly
);

export const hasOwnedActiveAdvisory = createSelector(
  getView,
  (view) => view.hasOwnedActiveAdvisory
);

export const hasActivePlusOnly = createSelector(
  getView,
  (view) => view.hasActivePlusOnly
);

export const hasOwnedActivePlus = createSelector(
  getView,
  (view) => view.hasOwnedActivePlus
);

export const hasSelfManagedAccounts = createSelector(
  getView,
  (view) => view.hasSelfManaged
);

export const hasSelfManagedAccountsOnly = createSelector(
  getView,
  (view) => view.hasSelfManagedOnly
);

export const isTracOnlyClient = createSelector(
  getView,
  (view) => view.isTracOnly
);

export const hasTracAccount = createSelector(
  getView,
  (view) => view.hasTracAccount
);

export const canViewHelpfulResources = createSelector(
  getView,
  isTracOnlyClient,
  hasTracAccount,
  (view, isTracOnlyClient, hasTracAccount) =>
    isTracOnlyClient ||
    (hasTracAccount && view.hasWorkplaceRetirementAccountsOnly)
);

export const getCollegeSavingsSelection = createSelector(
  hasAlaska529,
  hasMaryland529,
  hasNational529,
  (hasAlaska529, hasMaryland529, hasNational529) => {
    if (hasNational529) {
      return 'national529';
    }
    if (hasAlaska529) {
      return 'alaska529';
    }
    if (hasMaryland529) {
      return 'maryland529';
    }
    return 'all';
  }
);

export const hasMultipleCollegeSavings = createSelector(
  hasAlaska529,
  hasMaryland529,
  hasNational529,
  (hasAlaska529, hasMaryland529, hasNational529) => {
    let collegeSavingsCount =
      !!hasAlaska529 + !!hasMaryland529 + !!hasNational529;
    return collegeSavingsCount > 1;
  }
);

export const hasMultipleOwnedCollegeSavings = createSelector(
  hasOwnedAlaska529,
  hasOwnedMaryland529,
  hasOwnedNational529,
  (hasAlaska529, hasMaryland529, hasNational529) => {
    let collegeSavingsCount =
      !!hasAlaska529 + !!hasMaryland529 + !!hasNational529;
    return collegeSavingsCount > 1;
  }
);

export const get529OnlyViewType = createSelector(getView, (view) => {
  if (view.hasAlaska529Only) {
    return 'hasAlaska529Only';
  } else if (view.hasMaryland529Only) {
    return 'hasMaryland529Only';
  } else if (view.hasNational529Only) {
    return 'hasNational529Only';
  }
  return 'retail';
});

export const getThemeName = createSelector(getView, (view) => view.themeName);

const themeNameMap = {
  [THEME_ALASKA529]: 'ak529',
  [THEME_DEFAULT]: 'default',
  [THEME_MARYLAND529]: 'md529',
  [THEME_NATIONAL529]: 'nat529'
};

export const getThemeShortName = createSelector(getThemeName, (themeName) => {
  const shortName = themeNameMap[themeName];
  return shortName || 'default';
});

export const canViewTrustedContacts = createSelector(
  getActiveOwnedAccountsWithWorkplace,
  (accounts) => {
    return ableToViewTrustedContacts(accounts);
  }
);

export const canViewGrantViewAccess = createSelector(
  getActiveOwnedAccountsWithWorkplace,
  (accounts) => {
    return ableToViewGrantViewAcess(accounts)
  }
);

export const canViewContactInformation = createSelector(
  getActiveOwnedAccountsWithWorkplace,
  (accounts) => {
    return ableToViewContactInformation(accounts);
  }
);

export const canViewAccountRegistration = createSelector(
  hasCollegeSavingsOnly,
  (hasCollegeSavingsOnly) => {
    return !hasCollegeSavingsOnly && accountRegistrationEnabled();
  }
);

export const canViewBeneficiaries = createSelector(
  getActiveOwnedAccountsWithWorkplace,
  (accounts) => {
    return ableToViewBeneficiaries(accounts);
  }
);

export const hasOwnedAllProductTypes = createSelector(
  hasOwnedActivePlus,
  isBrokerageOwner,
  getHasOwnedCollegeSavings,
  hasOwnedMutualFunds,
  (
    hasOwnedActivePlus,
    isBrokerageOwner,
    hasOwnedCollegeSavings,
    hasOwnedMutualFunds
  ) => {
    return (
      hasOwnedActivePlus &&
      isBrokerageOwner &&
      hasOwnedCollegeSavings &&
      hasOwnedMutualFunds
    );
  }
);

export const isEligibleForRetailStatements = createSelector(
  getAccounts,
  (accounts) => {
    const isEligible = (account) => {
      const { owner, productType, isCentury: century } = account;
      return (
        owner &&
        isPrimaryParty(account) &&
        (isBrokerage(productType) ||
          isMutualFund(productType) ||
          isTracPlan(productType && !century))
      );
    };
    return accounts.some(isEligible);
  }
);

export const isEligibleForRetailConfirmsAndProspectus = createSelector(
  getAccounts,
  (accounts) => {
    const isEligible = (account) => {
      const { productType } = account;
      return isBrokerage(productType) || isMutualFund(productType);
    };
    return accounts.some(isEligible);
  }
);

export const isEligibleForRetailProxy = createSelector(
  getAccounts,
  (accounts) => {
    const isEligible = (account) => {
      const { productType } = account;
      return isMutualFund(productType);
    };
    return accounts.some(isEligible);
  }
);

export const isEligibleForAlaskaPaperless = createSelector(
  getAccounts,
  (accounts) => {
    const isEligible = (account) => {
      const { owner, productType, isRelated: related } = account;
      return (
        ((owner && isPrimaryParty(account)) || related) &&
        isAlaska529(productType) &&
        !isUGMAUTMA(account)
      );
    };
    return accounts.some(isEligible);
  }
);

export const isEligibleForMarylandPaperless = createSelector(
  getAccounts,
  (accounts) => {
    const isEligible = (account) => {
      const { owner, productType, isRelated: related } = account;

      return (
        ((owner && isPrimaryParty(account)) || related) &&
        isMaryland529(productType) &&
        !isUGMAUTMA(account)
      );
    };
    return accounts.some(isEligible);
  }
);

export const isEligibleForNationalPaperless = createSelector(
  getAccounts,
  (accounts) => {
    // National 529
    // Owned and Primary || Releated
    // Not UGMA UTMA
    const isEligible = (account) => {
      const { owner, productType, isRelated: related } = account;
      return (
        ((owner && isPrimaryParty(account)) || related) &&
        isNational529(productType) &&
        !isUGMAUTMA(account)
      );
    };
    return accounts.some(isEligible);
  }
);

export const isEligibleForRetailStatementsPaperless = createSelector(
  getAccounts,
  (accounts) => {
    const isEligible = (account) => {
      const { owner, productType, isRelated, accountTypeId } = account;
      return (
        owner ||
        (accountTypeId === EDUCATION_SAVINGS_ACCOUNTS &&
          isRelated) &&
        (isBrokerage(productType) || isMutualFund(productType))
      );
    };
    return accounts.some(isEligible);
  }
);

export const isEligibleForBrokerageCustodyStatements = createSelector(
    getAccounts,
    (accounts) => {

        return custodyStatementsEnabled()  && accounts.some(account => {
            const {  owner, productType, serviceIds } = account;
            return (
                owner && serviceIds && serviceIds.length  && serviceIds.some(id => id===61)
                &&
                (isBrokerage(productType))
            );
        });
    }
);

export const isEligibleForProxyShareHolder = createSelector(
  getAccounts,
  (accounts) => {
    const isEligible = (account) => {
      const { productType } = account;
      return isBrokerage(productType);
    };
    return accounts.some(isEligible);
  }
);

export const isEligibleForTaxFormsAndCommunications = createSelector(
  getAccounts,
  (accounts) => {
    const isEligible = (account) => {
      const { productType } = account;
      return isMutualFund(productType) || isBrokerage(productType);
    };
    return accounts.some(isEligible);
  }
);

export const isEligibleForMD529Paperless = createSelector(
  getOwnedActiveAccounts,
  (accounts) => {
    const isEligible = (account) => {
      const { owner, productType, isRelated: related } = account;

      return (owner || related) && isMaryland529(productType);
    };
    return accounts.some(isEligible);
  }
);

export const isEligibleForNAT529Paperless = createSelector(
  getOwnedActiveAccounts,
  (accounts) => {
    const isEligible = (account) => {
      const { owner, productType, isRelated: related } = account;

      return (owner || related) && isNational529(productType);
    };
    return accounts.some(isEligible);
  }
);

export const isEligibleForAK529Paperless = createSelector(
  getOwnedActiveAccounts,
  (accounts) => {
    const isEligible = (account) => {
      const { owner, productType, isRelated: related } = account;

      return (owner || related) && isAlaska529(productType);
    };
    return accounts.some(isEligible);
  }
);

export const canViewAutomaticTransactions = createSelector(
  hasBrokerageAdvantageOnly,
  hasActivePlusOnly,
  (doesHaveBrokerageAdvantageOnly, doesHaveActivePlusOnly) => {
    return !doesHaveBrokerageAdvantageOnly && !doesHaveActivePlusOnly;
  }
);

export const canViewAlertsAndNotifications = createSelector(
  getActiveOwnedAccountsWithWorkplace,
  (accounts) => {
    return ableToViewAlertsAndNotifications(accounts)
  }
);

export const canViewDividendsCapGains = createSelector(
  hasMutualFunds,
  hasBrokerage,
  (doesHaveMutualFunds, doesHaveBrokerage) => {
      return doesHaveMutualFunds || doesHaveBrokerage
  }
);

export const canViewCheckwriting = createSelector(
  getCheckwritingAccountGroups,
  (checkWritingAccountGroups) => {
    const { disabled, enabled } = checkWritingAccountGroups;
    return !isEmpty(disabled) || !isEmpty(enabled);
  }
);

export const hasNonTaxableMoneyMarketAccountsOnly = createSelector(
  getNonRetirementAccounts,
  (accounts) => {
    const isMoneyMarket = (account) => account.moneyMarket === true;
    return accounts.every((account) => isMoneyMarket(account));
  }
);

export const canViewCostBasisMethod = createSelector(
  hasCollegeSavingsOnly,
  hasBrokerageAdvantageOnly,
  hasActivePlusOnly,
  hasRetirementAccountsOnly,
  hasEsaOnly,
  hasNonTaxableMoneyMarketAccountsOnly,
  (
    doesHave529Only,
    doesHaveBrokerageAdvantageOnly,
    doesHaveActivePlusOnly,
    doesHaveRetirementAccountsOnly,
    doesHaveEsaOnly,
    doesHaveNonTaxableMoneyMarketAccountsOnly
  ) => {
    return (
      !doesHave529Only &&
      !doesHaveBrokerageAdvantageOnly &&
      !doesHaveActivePlusOnly &&
      !doesHaveRetirementAccountsOnly &&
      !doesHaveEsaOnly &&
      !doesHaveNonTaxableMoneyMarketAccountsOnly
    );
  }
);

//true if all the accounts are 529 Only, APP Only or a combination of both.
export const has529AppOnly = createSelector(
  getView,
  (view) => view.has529AppOnly
);

export const getCanViewIRACard = createSelector(
  getRMDData,
  getRequestStatus,
  getVisibleRetirementAccounts,
  (requiredMinimumDistribution, requestStatus, retirementAccounts) => {
    const callStatusKey = isRMDDirectEnabled()
      ? 'requiredMinimumDistributionSummary'
      : 'requiredMinimumDistribution';
    const receivedRmd =
      requestStatus[callStatusKey] === REQUEST_STATES.received;
    const filterPurgedRetirementAccounts = retirementAccounts.filter(
      (account) => !account.purgedAccount && !account.closedAccount
    );
    const oneRetirementAccountOwned = hasOwnedAccounts(
      filterPurgedRetirementAccounts
    );
    const hasIRADeceasedAndTracOnly = every(
      filterPurgedRetirementAccounts,
      (a) => isIRADeceased(a.accountTypeId) || isTracIRA(a.accountTypeId)
    );
    const hasViewOnlyRetirementAccountsOnly = isAllViewOnlyAccounts(
      filterPurgedRetirementAccounts
    );

    return (
      receivedRmd &&
      !requiredMinimumDistribution.customerEligible &&
      !requiredMinimumDistribution.customerPreEligible &&
      oneRetirementAccountOwned &&
      !hasIRADeceasedAndTracOnly &&
      !hasViewOnlyRetirementAccountsOnly
    );
  }
);

export const onAccountDetailsPage = createSelector(getRouter, (router) => {
  const pathname = router?.location?.pathname || '';

  let page =
    (pathname && pathname !== '/' && pathname.substring(1, pathname.length)) ||
    '/';

  return page === 'account-details';
});

export const onPerformancePage = createSelector(getRouter, (router) => {
  const pathname = router?.location?.pathname || '';

  let page =
    (pathname && pathname !== '/' && pathname.substring(1, pathname.length)) ||
    '/';

  return page === 'performance';
});

export const onAccountsPage = createSelector(getRouter, (router) => {
  const { pathname } = router.location;
  let page =
    (pathname && pathname !== '/' && pathname.substring(1, pathname.length)) ||
    '/';
  return page === 'accounts';
});
export const getHasMissingDOB = createSelector(
  getCustomer,
  getRequestStatus,
  (customer, requestStatus) => {
    const receivedCustomer = requestStatus.customer === REQUEST_STATES.received;
    const hasBirthDate = receivedCustomer && customer.birthDate;

    return receivedCustomer && !hasBirthDate;
  }
);

export const getHasMissingAddress = createSelector(
  getMailingAddress,
  getRequestStatus,
  (mailingAddress, requestStatus) => {
    const receivedMailingAddress =
      requestStatus.mailingAddress === REQUEST_STATES.received;
    const hasMailingAddress = !isEmpty(mailingAddress);

    return receivedMailingAddress && !hasMailingAddress;
  }
);

export const hasAllViewOnly529 = createSelector(
  get529Accounts,
  (collegeAccounts) => {
    return collegeAccounts.every(isViewOnlyAccount);
  }
);

export const hasAllViewOnlyNational529 = createSelector(
  getNational529Accounts,
  (nationalAccounts) => {
    return nationalAccounts.every(isViewOnlyAccount);
  }
);

export const hasAllViewOnlyMaryland529 = createSelector(
  getMaryland529Accounts,
  (marylandAccounts) => {
    return marylandAccounts.every(isViewOnlyAccount);
  }
);

export const hasAllViewOnlyAlaska529 = createSelector(
  getAlaska529Accounts,
  (alaskaAccounts) => {
    return alaskaAccounts.every(isViewOnlyAccount);
  }
);

export const hasAllViewOnlyBrokerage = createSelector(
  getBrokerageAccounts,
  (brokerageAccounts) => {
    return brokerageAccounts.every(isViewOnlyAccount);
  }
);

export const hasAllViewOnlyMutualFund = createSelector(
  getMutualFundAccounts,
  (mutualFundAccounts) => {
    return mutualFundAccounts.every(isViewOnlyAccount);
  }
);

export const hasAllViewOnlyActivePlus = createSelector(
  getActivePlusAccounts,
  (activePlusAccounts) => {
    return activePlusAccounts.every(isViewOnlyAccount);
  }
);

export const hasOnlyViewOnly = createSelector(
  getAccounts,
  (visibleAccounts) => {
    const nonPurgedAccounts = visibleAccounts.filter(
      (account) => !account.purgedAccount && !account.closedAccount
    );
    return isAllViewOnlyAccounts(nonPurgedAccounts);
  }
);

export const hasAllTerminatedAccounts = createSelector(
  getAccounts,
  (allAccounts) => {
    return allAccounts.every(
      (account) => account.purgedAccount || account.closedAccount
    );
  }
);

export const canViewDocumentDelivery = createSelector(
  getNonPurgedAccounts,
  getHasWorkPlaceRetirementAccountsOnly,
  (nonPurgedAccounts, hasWorkPlaceRetirementAccountsOnly) => {
    return (
      !isAllViewOnlyAccounts(nonPurgedAccounts) &&
      !hasWorkPlaceRetirementAccountsOnly
    );
  }
);

export const isPershingDependent = createSelector(
  getView,
  (view) => view.hasBrokerage || view.hasActivePlus
);

export const getPerformanceSelectedProductType = createSelector(
  getAccounts,
  () => ({ filter: { label: 'All' } }),
  hasActivePlus,
  hasBrokerage,
  (filteredAccountsArr, { filter }, hasActivePlus, hasBrokerage) => {
    if (
      (filter.label === ALL_LABEL && hasActivePlus && hasBrokerage) ||
      (filteredAccountsArr.some(({ productType }) =>
        isActivePlus(productType)
      ) &&
        filteredAccountsArr.some(({ productType }) => isBrokerage(productType)))
    ) {
      return 'activePlusBrokerage';
    }
    if (
      filter.label === APP_LABEL ||
      filteredAccountsArr.some(({ productType }) => isActivePlus(productType))
    ) {
      return 'activePlus';
    }
    if (
      filter.label === BROKERAGE_LABEL ||
      filteredAccountsArr.some(({ productType }) => isBrokerage(productType))
    ) {
      return 'brokerage';
    }
  }
);

export const getShowBrokerageAppCopy = createSelector(
  getPerformanceSelectedPeriod,
  getPerformanceSelectedPurpose,
  getAccountFilter,
  isPershingDependent,
  getPerformanceSelectedProductType,
  (period, purpose, accountFilter, pershingDependent, selectedProductType) => {
    const hasAppAndOrBrkSelected = selectedProductType;

    return (
      pershingDependent &&
      period === 'sinceInception' &&
      ((hasAppAndOrBrkSelected && accountFilter === 'product') ||
        (purpose.all && accountFilter === 'purpose'))
    );
  }
);

export const hasPerformanceAppBrokerageSelected = createSelector(
  getPerformanceSelectedProductType,
  (product) =>
    product === 'brokerage' ||
    product === 'activePlus' ||
    product === 'activePlusBrokerage'
);

export const getPerformanceMinInceptionDate = createSelector(
  getPerformanceAccounts,
  (performanceAccounts) => minInceptionDate(performanceAccounts)
);

export const isPershingDependentSelection = createSelector(
  isPershingDependent,
  getPerformanceSelectedProduct,
  (pershingDependent, products) =>
    pershingDependent &&
    (products.subset.brokerage || products.subset.activePlus || products.all)
);

export const periodsIsPershingDependent = createSelector(
  periods,
  isPershingDependentSelection,
  getPerformanceMinInceptionDate,
  getBrokerageAccounts,
  getActivePlusAccounts,
  (
    periods,
    isPershingDependent,
    performanceMinInception,
    brokerageAccounts,
    activePlusAccounts
  ) => {
    if (isPershingDependent) {
      // has Brokerage or APP and All or Brokerage or Active Plus is selected
      const brokerageMinInception = minInceptionDate(brokerageAccounts);
      const activePlusMinInception = minInceptionDate(activePlusAccounts);

      if (
        performanceMinInception === brokerageMinInception ||
        performanceMinInception === activePlusMinInception
      ) {
        // if Brokerage or APP have oldest inception date
        const endDate = periods.sinceInception.endDate;
        if (
          Time.withinDays(
            new Date(performanceMinInception),
            endDate,
            BRK_APP_OFFSET_DAYS
          )
        ) {
          // new start date if brokerage or APP account is > 155 weeks
          return true;
        }
      }
    }
    return false;
  }
);

export const getPeriods = createSelector(
  periodsIsPershingDependent,
  periods,
  (periodsIsPershingDependent, periods) => {
    if (periodsIsPershingDependent) {
      // new start date if brokerage or APP account is > 155 weeks
      const periodsCopy = cloneDeep(periods);
      periodsCopy.sinceInception.startDate = Time.daysFrom(
        periods.sinceInception.endDate,
        BRK_APP_OFFSET_DAYS
      );
      return periodsCopy;
    }
    return periods;
  }
);

export const getPerformanceSelected = createSelector(
  getPerformanceSelectedPeriod,
  getPeriods,
  (key, periods) => {
    let selectedPeriod = periods[key];
    return {
      title: selectedPeriod.label,
      value: key
    };
  }
);

/**
 *  * return true if there is NO errors in getting apiKey, userEntitlements, customer, workspacePreferences,
 *  accounts and purgedAccounts AND the requestStatus for all the entities IS NOT set to RECEIVED yet
 * @returns boolean
 */
export const isPageLoading = createSelector(
  getRequestStatus,
  ({
    apiKey,
    userEntitlements,
    customer,
    workspacePreferences,
    accounts,
    purgedAccounts
  }) => {
    const requestsStatusToCheck = [
      apiKey,
      userEntitlements,
      customer,
      workspacePreferences,
      accounts,
      purgedAccounts
    ];
    return (
      !requestsStatusToCheck.every(
        (entity) => entity === REQUEST_STATES.received
      ) &&
      !requestsStatusToCheck.some((entity) => entity === REQUEST_STATES.error)
    );
  }
);

export const isMobile = createSelector(
  getQueryParams,
  (params) => params.mobile && params.mobile === '1'
);

export const isProgramBenefits = createSelector(getRouter, (router) => {
  const { pathname } = router.location;

  let page =
    (pathname && pathname !== '/' && pathname.substring(1, pathname.length)) ||
    '/';
  return page === 'program-benefits';
});

export const getProductClassFromView = createSelector(getView, (view) =>
  getProductClass(view)
);

export const isEligibleToViewSubscriptions = createSelector(
  getAllAccounts,
  (allAccounts) => ableToViewSubscriptions(allAccounts)
);